import {
  createElement,
  Fragment,
  useState,
  useEffect,
  useRef,
  Children,
  useMemo,
  forwardRef,
  ReactNode,
  KeyboardEventHandler,
  useLayoutEffect,
  useImperativeHandle,
  AriaAttributes,
} from 'react';

import { useRefArray } from '../../hooks/useRefArray';
import { Icon } from '../Icon';
import { useDesignTokens, renderInlineCssVars } from '../../theme';
import {
  GrabberVertical,
  GrabberHorizontal,
  ArrowSmallRightBtn,
  ArrowSmallLeftBtn,
  ArrowSmallDownBtn,
  ArrowSmallUpBtn,
} from '@sas/react-svg';
import { useIsRTL } from '../../locale';
import { registerDependency } from '../../util/ServiceDependencies';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-Splitter-gui-icu.properties';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { usePersistedSplitter } from './persist/PersistedSplitterProvider';
import { postfixTestId } from '../../util/testId';
import { getLogger } from '@sas/nova-core/logging';
import useForceUpdate from '../../hooks/useForceUpdate';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { InvisibleText } from '../InvisibleText';
import { convertCSSSizeToNumber } from '../../util/dom';
import { getTopWindowAccessible } from '../../util';

import { SplitterRef, SplitterProps, SplitterSectionOptions, SplitterSize } from './types';
import cx from 'classnames';
import cn from './Splitter.css.json';
import './Splitter.css';

// Register Splitter component so that it will fetch required preference data
// eslint-disable-next-line @sas/i18n/jsx-to-localize
registerDependency('Splitter');

const useTextLocalizer = createUseTextLocalizer(bundle);

const logger = getLogger('Splitter');

type SectionDimsChangeType = 'toggle' | 'resize';

const useSplitterId = createUseHtmlId('splitter');

export const Splitter = forwardRef<SplitterRef, SplitterProps>(
  ({ persistPaneSizes = true, ...props }, ref) => {
    const isRTL = useIsRTL();
    const persistService = props.dataProvider ?? usePersistedSplitter();
    const orientation = props.orientation || 'horizontal';

    const sectionToggled = useRef(false);
    const sectionResized = useRef(false);
    let barCount = 0;
    const localize = useTextLocalizer();

    const [setSplitterContainer, getSplitterContainer] = useResizeObserverRef<HTMLDivElement>(
      () => {
        const container = getSplitterContainer();
        if (container)
          setContainerSize(
            container.getBoundingClientRect()[orientation === 'horizontal' ? 'width' : 'height']
          );
      }
    );

    const sectionBarPositions = useMemo(() => {
      if (!props.sections) return [];
      // Collapsible property decides the arrow position on splitter bar
      // First section which has collapsible=true get first preference as collapsible

      let barCount = 0;
      return props.sections.map((section, index) => {
        const nextSection = props.sections ? props.sections[index + 1] : undefined;
        if (
          props.sections &&
          props.sections.length >= 2 &&
          (section.collapsible || section.fullyCollapsible) &&
          index === 0
        ) {
          barCount += 1;
          return 'right' as const;
        } else if (
          props.sections?.length === 2 &&
          (section.collapsible || section.fullyCollapsible) &&
          index === 1 &&
          barCount === 0
        ) {
          return 'left' as const;
        } else if (
          props.sections?.length === 2 &&
          !(section.collapsible || section.fullyCollapsible) &&
          index === 0 &&
          nextSection &&
          !(nextSection.collapsible || nextSection.fullyCollapsible)
        ) {
          return 'right' as const;
        } else if (props.sections?.length === 3 && index === 2) {
          return 'left' as const;
        } else if (
          props.sections?.length === 3 &&
          index === 0 &&
          !!!section.collapsible &&
          !!!section.fullyCollapsible
        ) {
          if (!!!nextSection?.collapsible && !!!nextSection?.fullyCollapsible) {
            return 'right';
          } else {
            return undefined;
          }
        } else if (props.sections && props.sections.length > 3) {
          if (index === 0) return 'right';
          if (props.sections.length - 1 === index) {
            return 'left';
          } else if (props.sections.length - 2 === index) {
            return undefined;
          } else {
            return 'right';
          }
        } else {
          return undefined;
        }
      });
    }, [props.sections]);

    const [isDragging, setIsDragging] = useState(false);
    const [containerSize, setContainerSize] = useState<number | undefined>(undefined);
    const { refs, createSetRef } = useRefArray<HTMLElement>();
    const getSectionSizes = () => {
      return refs.map((element, index) => {
        if (!element) return NaN;
        const bounds = element.getBoundingClientRect();
        // only the center sections have padding which have to be removed to get the actual section size
        // sometimes the count of props.section may be different from the count of props.children although it maybe invalid.
        const padding =
          index === 0 || index === Children.count(props.children) - 1 ? 0 : paddingSize;
        return bounds[orientation === 'horizontal' ? 'width' : 'height'] - padding;
      });
    };

    // represents the persisted dimension of the Splitter panes.
    const persistedDimensions = useRef<SplitterSize>({
      paneSizes: [],
      previousBarPositions: [],
    });

    const rerenderOnFetch = useForceUpdate();

    const [{ sizes, previousSizes }, setSizes] = useState<{
      sizes: number[];
      previousSizes: number[];
    }>(() => {
      if (!props.sections) {
        return { sizes: [], previousSizes: [] };
      } else {
        return {
          sizes: props.sections.map((section: any) => {
            if (section.defaultSize) {
              const minSize = convertCSSSizeToNumber(section.minSize);
              if (section.defaultSize.indexOf('%') > -1) {
                return minSize;
              } else {
                return Math.max(convertCSSSizeToNumber(section.defaultSize), minSize);
              }
            }
            return -1;
          }),
          previousSizes: [],
        };
      }
    });

    useEffect(() => {
      async function fetchPersistedSize(id: string) {
        try {
          const persistedSize = await persistService?.getSize(id);
          if (persistedSize) {
            // incase the GET Size is delayed and user has changed the size,
            //  we do not want to store and apply older values
            if (persistedDimensions.current.paneSizes.length === 0) {
              persistedDimensions.current = persistedSize;
              rerenderOnFetch();
            }
          }
        } catch (e) {
          logger.error(`fetch persisted pane sizes for splitter with id ${id} failed`);
        }
      }
      if (persistPaneSizes && persistService && props.id) {
        fetchPersistedSize(props.id);
      }
    }, [persistPaneSizes, persistService, props.id, props.sections, rerenderOnFetch]);

    const updateSplitterPreferenceData = (id: string, oSplitterSizes: SplitterSize): void => {
      // the persistedDimensions ref is reflected with the changed Size regardless of persistence
      persistedDimensions.current = oSplitterSizes;
      persistService
        ?.setSize(id, oSplitterSizes)
        .catch(() => logger.error(`Save pane sizes for splitter with id ${id} failed`));
    };

    const convertPersistedSizesToPixels = (
      paneSizesInPercentage: string[],
      containerSize: number,
      sections?: readonly SplitterSectionOptions[]
    ): number[] => {
      // If a section has to be expanded to the min size, others have to be shrunk to avoid overlap
      const paneSizesInPixels = allocateWithProportions(
        paneSizesInPercentage.map((paneSize: string) =>
          paneSize &&
          paneSize.indexOf('%') > -1 &&
          paneSize.lastIndexOf('%') === paneSize.length - 1
            ? parseFloat(paneSize) / 100
            : 0
        ),
        containerSize ? containerSize : 1,
        sections
      );

      return paneSizesInPixels;
    };

    const convertPropsSizesToPixels = (
      sections: readonly SplitterSectionOptions[],
      containerSize: number
    ): number[] => {
      const paneSizesInPixels = sections.map((section) => {
        const minSize = convertCSSSizeToNumber(section.minSize);
        if (!!section.initialSplitterBarCollapsed && !isRenderedByProps.current) {
          if (section.fullyCollapsible) return 0;
          return minSize;
        }
        let size = -1;
        if (
          section.defaultSize &&
          section.defaultSize.indexOf('%') > -1 &&
          section.defaultSize.lastIndexOf('%') === section.defaultSize.length - 1
        ) {
          size = (parseInt(section.defaultSize) / 100) * (containerSize ? containerSize : 1);
        } else if (
          section.defaultSize &&
          section.defaultSize.indexOf('px') > -1 &&
          section.defaultSize.lastIndexOf('px') === section.defaultSize.length - 2
        ) {
          size = parseInt(section.defaultSize);
        } else if (
          section.defaultSize &&
          section.defaultSize.indexOf('rem') > -1 &&
          section.defaultSize.lastIndexOf('rem') === section.defaultSize.length - 3
        ) {
          size = convertCSSSizeToNumber(section.defaultSize);
        }
        return size === -1 ? -1 : Math.max(size, minSize);
      });
      return paneSizesInPixels;
    };

    // When reallocate the size with the exact size of each section, the sum of the calculated sizes are supposed to be 100% of the new container size.
    // If a section has to be expanded to the min size, others have to be shrunk to avoid overlap.
    const allocateWithProportions = (
      proportions: (number | undefined)[],
      containerSize: number,
      sections?: readonly SplitterSectionOptions[]
    ) => {
      const deltas = proportions.map((proportion, index) => {
        const minSize = convertCSSSizeToNumber(sections?.[index]?.minSize);
        if (proportion === undefined) {
          return 0;
        } else if (proportion === 0) {
          // section is collapsed
          return null;
        } else if (proportion < 0) {
          // section has not default size, implies it is 'auto'
          return undefined;
        } else {
          return proportion * containerSize - minSize;
        }
      });

      // totalNegativeDelta is always <= 0
      let totalNegativeDelta = deltas.reduce((a: number, b) => {
        if (b === undefined || b === null) {
          return a;
        } else if (b < 0) {
          return a + b;
        } else {
          return a;
        }
      }, 0);

      const newSizes = deltas.map((delta, index) => {
        const minSize = convertCSSSizeToNumber(sections?.[index]?.minSize);
        if (delta === undefined) {
          return -1;
        } else if (delta === null) {
          return 0;
        } else if (delta <= 0) {
          return minSize;
        } else {
          if (delta >= -totalNegativeDelta) {
            const newSize = minSize + delta + totalNegativeDelta;
            totalNegativeDelta = 0;
            return newSize;
          } else {
            totalNegativeDelta += delta;
            return minSize;
          }
        }
      });

      return newSizes;
    };

    useLayoutEffect(() => {
      if (
        containerSize &&
        props.id &&
        props.sections &&
        persistedDimensions.current.paneSizes.length > 0
      ) {
        // If spliiter's pane sizes are persisted against its id
        const paneSizes: string[] = persistedDimensions.current.paneSizes;
        const previousPaneSizes: string[] = persistedDimensions.current.previousBarPositions;

        // Convert persisted pane size proportions to pixels.
        const persistedSizes = convertPersistedSizesToPixels(
          paneSizes,
          containerSize,
          props.sections
        );
        const persistedPrevSizes = convertPersistedSizesToPixels(
          previousPaneSizes,
          containerSize,
          props.sections
        );
        setSizes(() => ({ sizes: persistedSizes, previousSizes: persistedPrevSizes }));
      } else if (containerSize && props.sections) {
        if (oldContainerSize) {
          // If a section has to be expanded to the min size, others have to be shrunk to avoid overlap
          const calculatedSizes: Array<number> = allocateWithProportions(
            sizes.map((size) => (size < 0 ? size : ((size / oldContainerSize) * 100) / 100)),
            containerSize,
            props.sections
          );
          setSizes(({ previousSizes }) => ({
            sizes: calculatedSizes,
            previousSizes,
          }));
        } else {
          const cssSizes = convertPropsSizesToPixels(props.sections, containerSize);
          isRenderedByProps.current = true;
          setSizes(({ previousSizes }) => ({
            sizes: cssSizes,
            previousSizes,
          }));
        }
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerSize, props.id]);

    const canPaneSizesBePersisted = persistPaneSizes && persistService && props.id;

    // sectionToggled and sectionResized are set to true iff canPaneSizesBePersisted is positive

    /**
     * This useEffect hook will fire PUT call to update pane sizes in preference service
     */
    useEffect(() => {
      if (!isDragging && props.id) {
        if (sectionToggled.current && JSON.stringify(oldSizes) !== JSON.stringify(sizes)) {
          sectionToggled.current = false;
          // Save preferences in % dimension if containerSize is undefined then store it in px dimension
          const oSplitterSizes = {
            paneSizes: sizes.map((sizeInPX: number) =>
              sizeInPX && containerSize ? `${(sizeInPX / containerSize) * 100}%` : `${sizeInPX}px`
            ),
            previousBarPositions: previousSizes.map((sizeInPX: number) =>
              sizeInPX && containerSize ? `${(sizeInPX / containerSize) * 100}%` : `${sizeInPX}px`
            ),
          };
          updateSplitterPreferenceData(props.id, oSplitterSizes);
        } else if (sectionResized.current) {
          sectionResized.current = false;
          // Save preferences in % dimension if containerSize is undefined then store it in px dimension
          const oSplitterSizes = {
            paneSizes: sizes.map((sizeInPX: number) =>
              sizeInPX && containerSize ? `${(sizeInPX / containerSize) * 100}%` : `${sizeInPX}px`
            ),
            previousBarPositions: previousSizes.map((sizeInPX: number) =>
              sizeInPX && containerSize ? `${(sizeInPX / containerSize) * 100}%` : `${sizeInPX}px`
            ),
          };
          updateSplitterPreferenceData(props.id, oSplitterSizes);
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging, sizes]);

    const isPaneCollapsed = (index: number) => {
      const section = props.sections?.[index];
      const minSize = convertCSSSizeToNumber(section?.minSize);
      const currentSize = sizes[index];
      return currentSize - minSize < 0.0001;
    };

    function resizeSection(action: {
      index: number;
      direction: 'left' | 'right';
      deltaPX: number;
    }) {
      const next = getSectionSizes();
      let deltaPX = action.deltaPX;
      const secondarySectionIndex = action.index + (action.direction === 'left' ? -1 : 1);

      if (props.sections) {
        // check minSize of the secondary section being resized
        {
          const section = props.sections[secondarySectionIndex];
          const nextSize = next[secondarySectionIndex] - deltaPX;
          const minSize: number = section.fullyCollapsible
            ? 0
            : convertCSSSizeToNumber(section.minSize);
          if (minSize > nextSize) {
            deltaPX -= minSize - nextSize;
          }
        }
        // check minSize of the primary section being resized
        {
          const section = props.sections[action.index];
          const nextSize = next[action.index] + deltaPX;
          const minSize = section.fullyCollapsible ? 0 : convertCSSSizeToNumber(section.minSize);
          if (minSize > nextSize) {
            deltaPX += minSize - nextSize;
          }
        }
      }

      next[action.index] += deltaPX;
      next[secondarySectionIndex] -= deltaPX;

      if (props.sections) {
        const section = props.sections[action.index];
        const minSize = convertCSSSizeToNumber(section.minSize);
        const currentSize = Math.round(sizes[action.index]);
        const currentSizeWithDeltaPX = currentSize + deltaPX;

        if (next[action.index] - minSize < 0.0001) {
          if (section.fullyCollapsible) {
            if (!isPaneCollapsed(action.index) && deltaPX < 0) {
              next[action.index] = 0;
              if (currentSizeWithDeltaPX === minSize) {
                next[secondarySectionIndex] += minSize;
              } else {
                next[secondarySectionIndex] += currentSizeWithDeltaPX;
              }
            } else if (isPaneCollapsed(action.index) && deltaPX > 0) {
              next[action.index] = minSize;
              next[secondarySectionIndex] -= containerSize ?? 0;
            } else if (currentSize === minSize && deltaPX < 0) {
              next[action.index] = 0;
              next[secondarySectionIndex] += currentSizeWithDeltaPX;
            }
          }
          props.onExpandCollapse?.(true, action.index);
        } else {
          props.onExpandCollapse?.(false, action.index);
        }
      }

      props.onBarMove?.(next);

      setSizes(({ previousSizes }) => ({
        sizes: next,
        previousSizes,
      }));

      return deltaPX !== 0;
    }

    const splitterBarSize = 2;
    const containerMinSize = () => {
      let size = 0;

      props.sections &&
        props.sections.forEach((section: SplitterSectionOptions) => {
          if (section.minSize) {
            size += convertCSSSizeToNumber(section.minSize);
          }
        });

      return size + barCount * splitterBarSize;
    };

    const dt = useDesignTokens();
    const splitterSectionPadding = dt.spacing.core.s;
    const splitterTokens = renderInlineCssVars(
      {
        bg: dt.c.colorUiBorder,
        'bar-size': `${splitterBarSize}px`,
        'min-size': `${containerMinSize()}px`,
        'icon-size': dt.font.size.xxxl,
        'section-padding': splitterSectionPadding,
      },
      'splitter'
    );
    const paddingSize = 2 * convertCSSSizeToNumber(splitterSectionPadding);

    const oldSizes = usePreviousValue(sizes);
    const oldContainerSize = usePreviousValue(containerSize);
    const oldDefaultValues = usePreviousValue(props.sections);
    const isRenderedByProps = useRef(false);
    const isRenderedByService = useRef(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
      if (
        containerSize &&
        persistPaneSizes &&
        props.id &&
        props.sections &&
        !isRenderedByService.current &&
        persistedDimensions.current.paneSizes.length > 0
      ) {
        // If spliiter's pane sizes are persisted against its id
        const paneSizes: string[] = persistedDimensions.current.paneSizes;
        const previousPaneSizes: string[] = persistedDimensions.current.previousBarPositions;

        // Convert persisted pane size proportions to pixels.
        const persistedSizes: number[] = convertPersistedSizesToPixels(
          paneSizes,
          containerSize,
          props.sections
        );
        // Convert persisted pane prev size proportions to pixels.
        const persistedPrevSizes: number[] = convertPersistedSizesToPixels(
          previousPaneSizes,
          containerSize,
          props.sections
        );

        if (JSON.stringify(oldSizes) !== JSON.stringify(persistedSizes)) {
          // Apply persisted Splitter Pane sizes
          isRenderedByService.current = true;
          setSizes(() => ({ sizes: persistedSizes, previousSizes: persistedPrevSizes }));
        }
      } else if (
        containerSize &&
        props.sections &&
        !isRenderedByProps.current &&
        !isRenderedByService.current
      ) {
        const cssSizes: number[] = convertPropsSizesToPixels(props.sections, containerSize);

        if (JSON.stringify(oldSizes) !== JSON.stringify(cssSizes)) {
          isRenderedByProps.current = true;
          setSizes(({ previousSizes }) => ({
            sizes: cssSizes,
            previousSizes,
          }));
        }
      } else if (
        JSON.stringify(oldDefaultValues) !== JSON.stringify(props.sections) &&
        props.sections &&
        containerSize
      ) {
        const cssSizes = convertPropsSizesToPixels(props.sections, containerSize);
        if (JSON.stringify(oldSizes) !== JSON.stringify(cssSizes)) {
          setSizes(({ previousSizes }) => ({
            sizes: cssSizes,
            previousSizes,
          }));
        }
      }
    });

    useImperativeHandle(ref, () => ({
      togglePane,
      isPaneCollapsed,
    }));

    const getSectionDefaultSizeInPixel = ({ defaultSize }: SplitterSectionOptions) => {
      if (!defaultSize) return -1;
      const isPercent =
        defaultSize.indexOf('%') > -1 && defaultSize.lastIndexOf('%') === defaultSize.length - 1;
      const isPixel =
        defaultSize.indexOf('px') > -1 && defaultSize.lastIndexOf('px') === defaultSize.length - 2;
      const isRem =
        defaultSize.indexOf('rem') > -1 &&
        defaultSize.lastIndexOf('rem') === defaultSize.length - 3;

      if (isPercent) {
        return (parseInt(defaultSize) / 100) * (containerSize ? containerSize : 1);
      } else if (isPixel) {
        return parseInt(defaultSize);
      } else if (isRem) {
        return convertCSSSizeToNumber(defaultSize);
      }
      return -1;
    };

    const togglePane = (index: number) => {
      const actSizes = getSectionSizes();
      setSizes(({ sizes, previousSizes }: { sizes: number[]; previousSizes: number[] }) => {
        const section = props.sections?.[index];
        const nextSizes = [...sizes],
          nextPrevSizes = [...previousSizes];
        if (!previousSizes[index] && !!section?.initialSplitterBarCollapsed) {
          previousSizes[index] = getSectionDefaultSizeInPixel(section);
        }
        const minSizeToPX = convertCSSSizeToNumber(section?.minSize);
        const minSize = section?.fullyCollapsible ? 0 : minSizeToPX, // section?.minSize ?? 0,
          currentSize = sizes[index] === -1 ? actSizes[index] : sizes[index];

        // According to current index take according pane size (next or previous)
        const paneIndex = index === 0 ? 1 : index - 1;
        const paneSize = sizes[paneIndex] === -1 ? actSizes[paneIndex] : sizes[paneIndex];
        const paneMinSize = convertCSSSizeToNumber(props.sections?.[paneIndex]?.minSize);
        let deltaSize,
          toggleSize = minSize,
          prevSize = currentSize;

        if (section?.fullyCollapsible) {
          if (currentSize - minSize < 0.0001) {
            // section is already collapsed
            deltaSize = Math.min(previousSizes[index] ?? 0 - currentSize, paneSize - paneMinSize); // delta size cannot be greater than available
            toggleSize = deltaSize + currentSize;
          } else {
            deltaSize = currentSize;
            toggleSize = 0;
          }
        } else {
          deltaSize =
            currentSize > minSize
              ? currentSize - minSize
              : !isNaN(previousSizes[index])
              ? Math.min(previousSizes[index] - minSize, paneSize - paneMinSize) // delta size cannot be greater than available
              : 0; // delta should be 0 instead of NaN when no previous size
        }

        if (deltaSize === 0) {
          deltaSize = section?.fullyCollapsible && minSizeToPX > 0 ? minSizeToPX : 20;
        }

        let isCollapsed = false;
        if (currentSize - minSize < 0.0001) {
          // section is already collapsed
          prevSize = minSize;
          toggleSize = deltaSize + currentSize;
          nextSizes[paneIndex] = paneSize - deltaSize;
        } else {
          nextSizes[paneIndex] = paneSize + deltaSize;
          isCollapsed = true;
        }
        nextPrevSizes[index] = prevSize;
        nextSizes[index] = toggleSize;
        sectionToggled.current = true;

        props.onExpandCollapse?.(isCollapsed, index);
        props.onBarMove?.(nextSizes);
        return Object.create({ sizes: [...nextSizes], previousSizes: [...nextPrevSizes] });
      });
    };

    return (
      <div
        ref={(element) => {
          setSplitterContainer(element);
        }}
        className={cx(cn.container[props.orientation || 'horizontal'], props.className)}
        style={{ ...splitterTokens, ...props.style }}
      >
        {Children.map(props.children, (child, index) => {
          const section = props.sections?.[index];

          // NOTE: when we are not dragging, convert all sizes to percents.
          //     | this allows the proportions to be preserved during a resize.
          //     | this is also more efficient than running JS during a resize to "follow" layout.
          // FIXME: UX Spec does not allow proportion to be maintained for defaultSizes that are absolute values
          //      | we can probably support that by default (but proportion maintaining could be a new feature?)
          const size =
            sizes[index] < 0
              ? undefined
              : isDragging || containerSize === undefined
              ? `${sizes[index]}px`
              : `${(sizes[index] / containerSize) * 100}%`;

          const minSize = section?.fullyCollapsible ? 0 : convertCSSSizeToNumber(section?.minSize);
          const isCollapsed = sizes[index] === -1 ? false : sizes[index] - minSize < 0.0001;
          const isLastSection = index === (props.sections ? props.sections.length - 1 : 0);

          const isBarPresent =
            sectionBarPositions[index] === 'right' || sectionBarPositions[index] === 'left';
          let barAriaLabel, barAriaLabelledBy;

          if (isBarPresent) {
            if (props.splitterBarAriaLabelledBy && props.splitterBarAriaLabelledBy.length > 0) {
              barAriaLabelledBy = props.splitterBarAriaLabelledBy[barCount];
            }
            if (
              props.splitterBarAriaLabels &&
              props.splitterBarAriaLabels.length > 0 &&
              !barAriaLabelledBy
            ) {
              barAriaLabel =
                props.splitterBarAriaLabels[barCount] ??
                localize('splitterBar.default.aria', { count: `${barCount + 1}` });
            } else if (!barAriaLabelledBy) {
              barAriaLabel = localize('splitterBar.default.aria', { count: `${barCount + 1}` });
            }
            barCount++;
          }

          return (
            <SplitterSection
              barAriaLabel={barAriaLabel}
              barAriaLabelledBy={barAriaLabelledBy}
              sectionAriaLabel={section?.ariaLabel}
              sectionAriaLabelledBy={section?.ariaLabelledBy}
              sectionAriaDescribedBy={section?.ariaDescribedBy}
              sectionLandmarkLabel={section?.landmarkLabel}
              testId={props.testId}
              paneIndex={index}
              isLastSection={isLastSection}
              minSize={convertCSSSizeToNumber(section?.minSize)}
              key={index}
              ref={createSetRef(index)}
              className={cx(
                section?.className,
                index !== 0 && index !== Children.count(props.children) - 1 && cn.hasPadding
              )} // give the center sections a padding to avoid overlap
              orientation={orientation}
              size={size}
              barPos={sectionBarPositions[index]}
              resizable={section?.resizable}
              hideGripIcon={(section?.hideGripIcon || props.hideGripIcon) ?? false}
              hideCollapseIcon={(section?.hideCollapseIcon || props.hideCollapseIcon) ?? false}
              hideSplitterBar={(section?.hideSplitterBar || props.hideSplitterBar) ?? false}
              collapsible={!!section?.collapsible}
              fullyCollapsible={!!section?.fullyCollapsible}
              isCollapsed={isCollapsed}
              onDragStart={() => {
                const next = getSectionSizes();
                if (next[index] !== 0 && next[index] !== convertCSSSizeToNumber(section?.minSize)) {
                  previousSizes[index] = next[index];
                }
                setSizes(({ previousSizes }: { previousSizes: number[] }) => ({
                  sizes: next,
                  previousSizes,
                }));
                setIsDragging(true);
              }}
              onDragEnd={(cause: SectionDimsChangeType) => {
                if (canPaneSizesBePersisted) {
                  if (cause === 'toggle') {
                    sectionToggled.current = true;
                  } else {
                    sectionResized.current = true;
                  }
                }
                setIsDragging(false);
              }}
              onDrag={(deltaPX) => {
                const direction = sectionBarPositions[index];

                if (direction) {
                  if (orientation === 'horizontal') {
                    deltaPX = isRTL ? -deltaPX : deltaPX;
                  }

                  return resizeSection({ index, direction, deltaPX });
                }
                return false;
              }}
              onToggleCollapsed={() => {
                togglePane(index);
              }}
              onKeyDown={(event) => {
                if (
                  section &&
                  section.resizable === false &&
                  event.key !== ' ' &&
                  event.key !== 'Enter'
                )
                  return;
                const direction = sectionBarPositions[index];
                switch (event.key) {
                  case 'ArrowLeft': {
                    if (orientation === 'horizontal' && direction) {
                      if (direction === 'right')
                        resizeSection({ index, direction, deltaPX: isRTL ? 20 : -20 });
                      else resizeSection({ index, direction, deltaPX: isRTL ? -20 : 20 });
                      sectionResized.current = true;
                    }
                    break;
                  }
                  case 'ArrowRight': {
                    if (orientation === 'horizontal' && direction) {
                      if (direction === 'right')
                        resizeSection({ index, direction, deltaPX: isRTL ? -20 : 20 });
                      else resizeSection({ index, direction, deltaPX: isRTL ? 20 : -20 });
                      sectionResized.current = true;
                    }
                    break;
                  }
                  case 'ArrowUp': {
                    if (orientation === 'vertical' && direction) {
                      if (direction === 'right') resizeSection({ index, direction, deltaPX: -20 });
                      else resizeSection({ index, direction, deltaPX: 20 });
                      sectionResized.current = true;
                    }
                    break;
                  }
                  case 'ArrowDown': {
                    if (orientation === 'vertical' && direction) {
                      if (direction === 'right') resizeSection({ index, direction, deltaPX: 20 });
                      else resizeSection({ index, direction, deltaPX: -20 });
                      sectionResized.current = true;
                    }
                    break;
                  }
                  case ' ':
                  case 'Enter': {
                    if (
                      section &&
                      (section.collapsible || section.fullyCollapsible) &&
                      (index === 0 || isLastSection)
                    )
                      togglePane(index);
                    break;
                  }
                  case 'Home': {
                    if (direction) resizeSection({ index, direction, deltaPX: -9999 });
                    sectionToggled.current = true;
                    props.onExpandCollapse?.(!isCollapsed, index);
                    break;
                  }
                  case 'End': {
                    if (direction) resizeSection({ index, direction, deltaPX: 9999 });
                    sectionToggled.current = true;
                    props.onExpandCollapse?.(!isCollapsed, index);
                    break;
                  }

                  default:
                    return;
                }
                event.preventDefault();
              }}
            >
              {child}
            </SplitterSection>
          );
        })}
      </div>
    );
  }
);

const SplitterSection = forwardRef<
  HTMLElement,
  {
    className: string | undefined;
    /**
     * Describes the position of the bar relative to this section (in LTR).
     */
    barPos: 'left' | 'right' | undefined;
    orientation: 'horizontal' | 'vertical';
    collapsible: boolean;
    fullyCollapsible: boolean;
    size: number | string | undefined;
    resizable: boolean | undefined;
    hideGripIcon: boolean;
    hideCollapseIcon: boolean;
    hideSplitterBar: boolean;
    onDragStart(): void;
    onDragEnd(cause: SectionDimsChangeType): void;
    onDrag(deltaSize: number): boolean;
    onToggleCollapsed(): void;
    children: ReactNode;
    onKeyDown: KeyboardEventHandler;
    paneIndex: number;
    isLastSection: boolean | undefined;
    minSize: number;
    testId: null | undefined | string;
    isCollapsed?: boolean;
    barAriaLabel?: AriaAttributes['aria-label'];
    barAriaLabelledBy?: AriaAttributes['aria-labelledby'];
    sectionAriaLabel?: AriaAttributes['aria-label'];
    sectionAriaLabelledBy?: AriaAttributes['aria-labelledby'];
    sectionAriaDescribedBy?: AriaAttributes['aria-describedby'];
    sectionLandmarkLabel?: string;
  }
>(function SplitterSection({ resizable = true, ...props }, ref) {
  const { startDrag } = useDeltaDrag({
    onChange({ deltaX, deltaY }) {
      let delta = props.orientation === 'vertical' ? deltaY : deltaX;
      if (props.barPos === 'left') {
        delta *= -1;
      }
      return props.onDrag(delta);
    },
    onCommit: props.onDragEnd,
  });
  const isRTL = useIsRTL();
  const collapseIcons = {
    horizontal: {
      left: {
        icon: ArrowSmallRightBtn,
        offset: isRTL ? { right: '-0.2rem' } : { left: '-0.2rem' },
        closeIcon: ArrowSmallLeftBtn,
        closeOffset: isRTL ? { left: '-0.2rem' } : { right: '-0.2rem' }, // { left: '-0.5rem' },
      },
      right: {
        icon: ArrowSmallLeftBtn,
        offset: isRTL ? { left: '-0.2rem' } : { right: '-0.2rem' },
        closeIcon: ArrowSmallRightBtn,
        closeOffset: isRTL ? { right: '-0.2rem' } : { left: '-0.2rem' }, // { right: '-0.5rem' },
      },
    },
    vertical: {
      left: {
        icon: ArrowSmallDownBtn,
        offset: { top: '-0.3rem' },
        closeIcon: ArrowSmallUpBtn,
        closeOffset: { bottom: '-0.5rem' },
      },

      right: {
        icon: ArrowSmallUpBtn,
        offset: { bottom: '-0.5rem' },
        closeIcon: ArrowSmallDownBtn,
        closeOffset: { top: '-0.3rem' },
      },
    },
  };
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  let ariaDescription =
    props.orientation === 'horizontal'
      ? localize('splitterBar.verticalResize.aria')
      : localize('splitterBar.horizontalResize.aria');
  ariaDescription += props.collapsible ? ` ${localize('splitterBar.collapsible.aria')}` : '';
  const ariaValueNow = parseInt(props.size ? `${props.size}` : '0');

  const isCollapsed = props.isCollapsed ?? false;

  const splitterBarDescriptionId = useSplitterId();

  const splitterBar = (props.barPos === 'left' || props.barPos === 'right') && (
    <div
      data-testid={postfixTestId(props.testId, 'splitterBar')}
      className={cx(
        cn.bar[props.orientation],
        props.hideSplitterBar && cn.hideSplitterBar,
        !resizable && cn.resizable
      )}
      role="separator"
      tabIndex={resizable || props.collapsible || props.fullyCollapsible ? 0 : -1}
      // TODO: a11y aria attributes (check SAS UX Spec, it is a bit different from WAI-ARIA)
      onMouseDown={(event) => {
        if (!resizable) return;
        // 0 is left mouse button
        if (event.button !== 0) return;
        // If the event comes from this element focus the element so it receives the focus indicator on mouse down when
        // a custom focus indicator is set.
        if (event.target === event.currentTarget) {
          (event?.currentTarget as HTMLElement).focus();
        }
        startDrag([event.clientX, event.clientY]);
        const targetElement = (event?.target as HTMLElement)?.tagName;
        if (targetElement === 'path' || targetElement === 'svg') return;
        props.onDragStart();
        // don't start a real browser drag and drop
        event.preventDefault();
      }}
      onKeyDown={(event) => props.onKeyDown(event)}
      aria-label={props.barAriaLabel}
      aria-labelledby={props.barAriaLabelledBy}
      aria-describedby={splitterBarDescriptionId}
      aria-orientation={props.orientation === 'horizontal' ? 'vertical' : 'horizontal'}
      aria-valuemin={props.minSize}
      aria-valuemax={100}
      aria-valuenow={ariaValueNow}
    >
      <InvisibleText id={splitterBarDescriptionId}>{ariaDescription}</InvisibleText>
      {!props.hideGripIcon && resizable && (
        <Icon
          className={cn.resizeIcon}
          tokens={() => {
            return {
              fg:
                dt.themeVariant === 'high-contrast' && !props.hideSplitterBar
                  ? dt.c.colorTextInverse
                  : dt.c.colorTextDefault,
            };
          }}
          icon={props.orientation === 'horizontal' ? GrabberVertical : GrabberHorizontal}
        />
      )}
      {(props.collapsible || props.fullyCollapsible) &&
        !props.hideCollapseIcon &&
        (props.paneIndex === 0 || props.isLastSection) && (
          <div
            className={cn.collapseIcon[props.orientation]}
            style={
              !!isCollapsed
                ? collapseIcons[props.orientation][props.barPos].closeOffset
                : collapseIcons[props.orientation][props.barPos].offset
            }
          >
            <Icon
              icon={
                !!isCollapsed
                  ? collapseIcons[props.orientation][props.barPos].closeIcon
                  : collapseIcons[props.orientation][props.barPos].icon
              }
              tokens={() => {
                return {
                  fg: dt.c.colorTextDefault,
                };
              }}
              onClick={(event) => {
                props.onToggleCollapsed();
                event.stopPropagation();
              }}
            />
          </div>
        )}
    </div>
  );

  return (
    <Fragment>
      {props.barPos === 'left' && splitterBar}
      <section
        role={props.sectionLandmarkLabel ? 'region' : undefined}
        ref={ref}
        className={cx(cn.section, props.className)}
        style={{
          flexBasis: props.size === undefined ? 'auto' : props.size,
          flexGrow: props.size === undefined ? 1 : 0,
          flexShrink: props.size === undefined ? 1 : 0,
        }}
        aria-label={!props.sectionAriaLabelledBy ? props.sectionAriaLabel : undefined}
        aria-labelledby={props.sectionAriaLabelledBy}
        aria-describedby={props.sectionAriaDescribedBy}
        data-landmark-label={props.sectionLandmarkLabel}
      >
        {props.children}
      </section>
      {props.barPos === 'right' && splitterBar}
    </Fragment>
  );
});

/**
 * TODO: mostly copied/striped down from `useSliderDrag` in Slider.
 */
function useDeltaDrag(props: {
  readonly onChange: (event: { deltaX: number; deltaY: number }) => boolean;
  readonly onCommit: (cause: SectionDimsChangeType) => void;
}): {
  /**
   * Start a drag operation.
   *
   * @param dragStartClientPos The [clientX, clientY] the drag started on.
   */
  startDrag(dragStartClientPos: [number, number]): void;
  abortDrag(): void;
  isDragging: boolean;
} {
  const [isDragging, setIsDragging] = useState(false);
  const refLastStart = useRef([NaN, NaN]);

  useEffect(() => {
    if (!isDragging) return;

    function delta(clientX: number, clientY: number) {
      const delta = {
        deltaX: clientX - refLastStart.current[0],
        deltaY: clientY - refLastStart.current[1],
      };
      return delta;
    }

    function onDrag(e: MouseEvent) {
      if (!props.onChange) return;
      const onChange = props.onChange;
      const moved = onChange(delta(e.clientX, e.clientY));
      // Only update the last start ref when the bar really moved. So when over dragging, the ref last start could stay in the boundry.
      if (moved) refLastStart.current = [e.clientX, e.clientY];
    }
    function onDragEnd(e: MouseEvent) {
      // only respond to left buttons being lifted
      if (e.button !== 0) return;
      const targetElement = (e?.target as HTMLElement)?.tagName;

      const position = delta(e.clientX, e.clientY);

      // NOTE: create a copy to prevent mutation for the onCommit handler
      props.onChange({ ...position });

      props.onCommit(targetElement === 'path' || targetElement === 'svg' ? 'toggle' : 'resize');

      setIsDragging(false);
    }

    function onDragLeave(_e: MouseEvent) {
      setIsDragging(false);
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
    }

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onDragEnd);
    if (getTopWindowAccessible()) {
      top!.addEventListener('mouseup', onDragLeave);
    }

    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
      if (getTopWindowAccessible()) {
        top!.removeEventListener('mouseup', onDragLeave);
      }
    };
  }, [props, isDragging]);

  return {
    startDrag(dragStartClientPos) {
      refLastStart.current = dragStartClientPos;
      setIsDragging(true);
    },
    abortDrag() {
      setIsDragging(false);
    },
    isDragging,
  };
}
